/**
 * React hook for managing users of the currently selected game
 * 
 * This hook automatically uses the selected game from userDataStore
 * to fetch and manage game users with automatic caching and error handling.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getSelectedGame, UserProfile } from './stores/unifiedUserStore';
import { useToast } from '@/hooks/use-toast';

interface UseSelectedGameUsersOptions {
  autoFetch?: boolean;
}

interface UseSelectedGameUsersReturn {
  users: UserProfile[];
  loading: boolean;
  error: string | null;
  selectedGame: any | null;
  fetchUsers: () => Promise<void>;
  getUserById: (userId: string) => UserProfile | null;
  getUsersByRole: (role: string) => UserProfile[];
  refresh: () => Promise<void>;
}

/**
 * Hook to manage users of the currently selected game
 * @param options - Configuration options
 * @returns Object with users data and utility functions
 */
export function useSelectedGameUsers(options: UseSelectedGameUsersOptions = {}): UseSelectedGameUsersReturn {
  const { selectedGame, getGameMembers, getUserNameById, isLoading, error, refreshData } = useSimplifiedUserData();
  
  // Get users for the currently selected game
  const users = selectedGame ? getGameMembers(selectedGame.id) : [];
  
  // Simple utility functions
  const fetchUsers = useCallback(async () => {
    await refreshData();
  }, [refreshData]);

  const getUserById = useCallback((userId: string): UserProfile | null => {
    return users.find(user => user.id === userId) || null;
  }, [users]);

  const getUsersByRole = useCallback((role: string): UserProfile[] => {
    return users.filter(user => (user as any).role === role);
  }, [users]);

  const refresh = useCallback(async () => {
    await refreshData();
  }, [refreshData]);

  return {
    users,
    loading: isLoading,
    error: error,
    selectedGame,
    fetchUsers,
    getUserById,
    getUsersByRole,
    refresh,
  };
} 